import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ClassicChart from "@/pages/ClassicChart";
import {
  Users, Stethoscope, Pill, FlaskConical, Calendar, ChevronRight,
  Activity, AlertCircle, Sparkles, Loader2, ClipboardList, ArrowRight,
  RefreshCw, MessageSquare, ArrowUpRight, FileText,
} from "lucide-react";

const SPEC_ACCENT = {
  nephrology: "#4ADE80",  cardiology: "#F87171",  neurology: "#A78BFA",
  oncology: "#FBBF24",    endocrinology: "#8B5CF6", pulmonology: "#38BDF8",
  gastroenterology: "#F97316", rheumatology: "#EC4899", infectious_disease: "#22C55E",
  patient: "#94A3B8",     general: "#60A5FA",
};

const SEVERITY_COLOR = { mild: "#4ADE80", moderate: "#FBBF24", severe: "#F87171" };

/**
 * CareTeamHome — the redesigned patient landing page.
 *
 * Replaces the fragmented 9-tab specialty jump with ONE calm dashboard:
 *   1. Summary strip           — "cared for by N doctors across M specialties"
 *   2. Active conditions row   — deduped diagnoses (from encounter.assessment first-liner)
 *   3. Care team cards         — one per doctor with encounters + Rx + orders + next fu
 *   4. Unified timeline        — every event across every doctor
 *
 * Design intent: patient understands their whole story in under 30 seconds.
 */
export default function CareTeamHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState(() =>
    (typeof window !== "undefined" && localStorage.getItem("chart_view")) || "modern"
  );
  const [ct, setCt] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");   // "all" | specialty id
  const [update, setUpdate] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(true);

  const loadUpdate = useCallback(async (force = false) => {
    setUpdateLoading(true);
    try {
      const { data } = await api.get(`/patient/care-update${force ? "?force=true" : ""}`);
      setUpdate(data);
    } catch { /* soft */ } finally { setUpdateLoading(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cd }, { data: td }] = await Promise.all([
        api.get("/patient/care-team"),
        api.get("/patient/care-team/timeline?limit=30"),
      ]);
      setCt(cd);
      setTimeline(td.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load care team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadUpdate(); }, [load, loadUpdate]);

  const activeConditions = useMemo(() => {
    if (!ct) return [];
    // Prefer coded diagnoses (ICD-10) — dedupe by code, take latest
    const coded = (ct.diagnoses || []).map((d) => ({
      title: d.title,
      code: d.code,
      category: d.category,
      severity: null,
      specialty: d.added_by_specialty || "general",
      doctor: d.added_by_name || "—",
      is_coded: true,
    }));
    // Union with encounter chief-complaint style entries the coded set does not cover
    const seen = new Set(coded.map((c) => (c.title || "").toLowerCase()));
    const out = [...coded];
    for (const card of ct.care_team || []) {
      for (const enc of card.encounters || []) {
        const key = (enc.chief_complaint || "").trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({
          title: enc.chief_complaint,
          code: null,
          category: null,
          severity: enc.severity,
          specialty: enc.specialty || card.doctor.specialty,
          doctor: card.doctor.name,
          is_coded: false,
        });
        if (out.length >= 8) break;
      }
      if (out.length >= 8) break;
    }
    return out;
  }, [ct]);

  if (loading) {
    return (
      <div className="ros-card flex items-center gap-2" data-testid="care-team-loading">
        <Loader2 className="animate-spin" size={16} /> Loading your care team…
      </div>
    );
  }

  if (!ct) return null;
  const s = ct.summary;

  // Show classic OPD-style chart if toggled — printable, senior-doctor-friendly layout
  if (viewMode === "classic") {
    return (
      <div className="space-y-3" data-testid="care-team-home">
        <div className="flex items-center justify-end gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#7A8480]">View</span>
          <div className="inline-flex rounded-full bg-[#1C201E] p-0.5 border border-[#272E2B]">
            <button onClick={() => { setViewMode("modern"); localStorage.setItem("chart_view", "modern"); }}
                    data-testid="chart-view-modern"
                    className="text-xs px-3 py-1 rounded-full text-[#A3ACA7] hover:text-white">
              Modern
            </button>
            <button data-testid="chart-view-classic"
                    className="text-xs px-3 py-1 rounded-full bg-[#4ADE80] text-black font-semibold">
              Classic OPD
            </button>
          </div>
        </div>
        <ClassicChart />
      </div>
    );
  }

  const allCards = ct.care_team || [];
  const cards = filter === "all"
    ? allCards
    : allCards.filter((c) => c.doctor.specialty === filter);
  const filteredTimeline = filter === "all"
    ? timeline
    : timeline.filter((e) => (e.specialty || "") === filter);
  const filteredConditions = filter === "all"
    ? activeConditions
    : activeConditions.filter((c) => c.specialty === filter);
  const specialtyChips = (s.specialties || []);

  return (
    <div className="space-y-6" data-testid="care-team-home">
      {/* View toggle — Modern vs Classic OPD (senior-doctor friendly) */}
      <div className="flex items-center justify-end gap-2">
        <span className="text-[10px] uppercase tracking-wider text-[#7A8480]">View</span>
        <div className="inline-flex rounded-full bg-[#1C201E] p-0.5 border border-[#272E2B]"
             data-testid="chart-view-toggle">
          <button data-testid="chart-view-modern"
                  className="text-xs px-3 py-1 rounded-full bg-[#4ADE80] text-black font-semibold">
            Modern
          </button>
          <button onClick={() => { setViewMode("classic"); localStorage.setItem("chart_view", "classic"); }}
                  data-testid="chart-view-classic"
                  className="text-xs px-3 py-1 rounded-full text-[#A3ACA7] hover:text-white">
            Classic OPD
          </button>
        </div>
      </div>

      {/* 1. Summary strip */}
      <div className="ros-card"
           style={{ background: "linear-gradient(120deg, #0F1312 0%, #101715 100%)" }}
           data-testid="ct-summary">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryPill label="Doctors" value={s.doctor_count}
                       icon={Users} accent="#4ADE80" testid="ct-sum-doctors" />
          <SummaryPill label="Specialties" value={s.specialty_count}
                       icon={Stethoscope} accent="#38BDF8" testid="ct-sum-specialties" />
          <SummaryPill label="Active Rx" value={s.active_medications}
                       icon={Pill} accent="#8B5CF6" testid="ct-sum-rx" />
          <SummaryPill label="Encounters" value={s.total_encounters}
                       icon={ClipboardList} accent="#FBBF24" testid="ct-sum-encounters" />
          <SummaryPill label="Pending tests" value={s.pending_orders}
                       icon={FlaskConical} accent="#F87171" testid="ct-sum-orders" />
        </div>
        <div className="mt-3 text-xs text-[#A3ACA7]">
          You&rsquo;re being cared for by <strong className="text-white">{s.doctor_count} doctors</strong>{" "}
          across <strong className="text-white">{s.specialty_count} specialties</strong>. Every
          prescription, test, and assessment is consolidated below.
        </div>
      </div>

      {/* 1b. AI Care Update — plain-English recap of the last 30 days */}
      <CareUpdateCard update={update} loading={updateLoading} onRefresh={() => loadUpdate(true)} />

      {/* 1c. Filter chips — narrow the view to one specialty */}
      {specialtyChips.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="ct-filter-chips">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mr-1">
            Filter by specialty
          </span>
          <button onClick={() => setFilter("all")}
                  data-testid="ct-filter-all"
                  className={`text-xs px-3 py-1 rounded-full font-bold transition-colors ${
                    filter === "all"
                      ? "bg-white text-black"
                      : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                  }`}>
            All ({allCards.length})
          </button>
          {specialtyChips.map((spec) => {
            const on = filter === spec;
            const accent = SPEC_ACCENT[spec] || "#94A3B8";
            const count = allCards.filter((c) => c.doctor.specialty === spec).length;
            return (
              <button key={spec} onClick={() => setFilter(spec)}
                      data-testid={`ct-filter-${spec}`}
                      className={`text-xs px-3 py-1 rounded-full font-bold transition-colors ${
                        on ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                      }`}
                      style={on ? { background: accent } : {}}>
                {spec.replace("_", " ")} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* 2. Active conditions row */}
      {filteredConditions.length > 0 && (
        <div className="space-y-2" data-testid="ct-conditions">
          <div className="flex items-center gap-2">
            <div className="ros-label">Active Conditions</div>
            <span className="text-[10px] text-[#7A8480]">
              · latest across {filter === "all" ? "all your visits" : filter.replace("_", " ")}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {filteredConditions.map((c, i) => (
              <div key={i} className="ros-card space-y-1"
                   style={{ borderColor: `${SEVERITY_COLOR[c.severity] || "#272E2B"}80` }}
                   data-testid={`ct-condition-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider"
                        style={{ color: SEVERITY_COLOR[c.severity] || (c.is_coded ? "#4ADE80" : "#A3ACA7") }}>
                    {c.is_coded
                      ? <>ICD-10 · {c.code}</>
                      : <>{c.severity || "recorded"} · {c.specialty}</>}
                  </span>
                  {c.is_coded && c.category && (
                    <span className="text-[9px] text-[#7A8480] uppercase tracking-wider">
                      {c.category}
                    </span>
                  )}
                </div>
                <div className="text-sm text-white leading-tight">{c.title}</div>
                <div className="text-[10px] text-[#7A8480]">
                  {c.is_coded ? <>Coded by {c.doctor}</> : <>Reviewed by {c.doctor}</>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Care team cards */}
      <div className="space-y-3" data-testid="ct-cards">
        <div className="flex items-center justify-between">
          <div className="ros-label">Your Care Team</div>
          <button onClick={() => nav("/encounters")}
                  data-testid="ct-new-checkin"
                  className="text-xs text-[#4ADE80] hover:underline">
            + Start a new check-in
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {cards.map((card) => (
            <DoctorCard key={card.doctor.id} card={card} onOpenEncounter={(id) => nav(`/encounters/${id}`)} />
          ))}
        </div>
      </div>

      {/* 4. Unified timeline */}
      <div className="space-y-2" data-testid="ct-timeline">
        <div className="ros-label">Recent Activity · All Doctors</div>
        <div className="ros-card space-y-2 max-h-96 overflow-y-auto">
          {timeline.length === 0 ? (
            <div className="text-xs text-[#7A8480]">No recorded events yet.</div>
          ) : timeline.map((ev, i) => (
            <TimelineRow key={`${ev.kind}-${ev.id}-${i}`} ev={ev} />
          ))}
        </div>
      </div>
    </div>
  );
}


function SummaryPill({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="flex items-center gap-3 min-w-0" data-testid={testid}>
      <div className="w-9 h-9 rounded-2xl flex items-center justify-center shrink-0"
           style={{ background: `${accent}18`, border: `1px solid ${accent}40` }}>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{label}</div>
        <div className="font-heading text-xl text-white leading-tight">{value}</div>
      </div>
    </div>
  );
}


function DoctorCard({ card, onOpenEncounter }) {
  const [expand, setExpand] = useState(false);
  const accent = SPEC_ACCENT[card.doctor.specialty] || "#60A5FA";
  const initials = (card.doctor.name || "?").split(" ")
    .slice(-2).map((s) => s[0]).join("").toUpperCase();
  const isSelf = card.doctor.id === "self_recorded";

  return (
    <div className="ros-card space-y-3"
         style={{ borderColor: `${accent}40` }}
         data-testid={`ct-doctor-${card.doctor.id}`}>
      {/* Doctor header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center font-heading text-sm text-black shrink-0"
             style={{ background: accent }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-heading text-base text-white leading-tight">
              {card.doctor.name}
            </div>
            {card.next_followup && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{ background: `${accent}18`, color: accent }}>
                Next {card.next_followup}
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>
            {isSelf ? "Self-reported check-ins" : card.doctor.specialty.replace("_", " ")}
          </div>
        </div>
      </div>

      {/* Counters strip */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <MiniCounter label="Encounters" value={card.encounter_count} accent={accent} />
        <MiniCounter label="Active Rx" value={card.prescription_count} accent={accent} />
        <MiniCounter label="Orders" value={card.order_count} accent={accent} />
      </div>

      {/* Recent encounter preview */}
      {card.recent_encounter && (
        <button onClick={() => onOpenEncounter(card.recent_encounter.id)}
                data-testid={`ct-open-enc-${card.doctor.id}`}
                className="w-full text-left rounded-2xl p-2.5 bg-[#1C201E] hover:bg-[#272E2B] transition-colors">
          <div className="text-[10px] uppercase tracking-wider"
               style={{ color: SEVERITY_COLOR[card.recent_encounter.severity] || "#A3ACA7" }}>
            Latest visit · {card.recent_encounter.severity || "recorded"}
          </div>
          <div className="text-sm text-white leading-tight mt-0.5">
            {card.recent_encounter.chief_complaint}
          </div>
          {card.recent_encounter.assessment && (
            <div className="text-xs text-[#A3ACA7] mt-1 line-clamp-2">
              {card.recent_encounter.assessment}
            </div>
          )}
          <div className="mt-1 text-[10px] text-[#7A8480] flex items-center gap-1">
            Open <ChevronRight size={10} />
          </div>
        </button>
      )}

      {/* Expandable details */}
      {(card.prescriptions.length > 0 || card.orders.length > 0) && (
        <button onClick={() => setExpand((v) => !v)}
                data-testid={`ct-expand-${card.doctor.id}`}
                className="text-[10px] text-[#7A8480] hover:text-white uppercase tracking-wider flex items-center gap-1">
          {expand ? "▲ Hide details" : "▼ Show all Rx & orders"}
        </button>
      )}
      {expand && (
        <div className="space-y-2">
          {card.prescriptions.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">
                Prescriptions
              </div>
              <ul className="space-y-1">
                {card.prescriptions.map((m) => (
                  <li key={m.id} className="text-xs text-[#A3ACA7] flex items-start gap-2">
                    <Pill size={11} className="mt-0.5" style={{ color: accent }} />
                    <span>
                      <span className="text-white">{m.generic_name || m.name}</span>
                      {" · "}{m.dose} {m.frequency}
                      {m.indication && <span className="text-[#7A8480]"> ({m.indication})</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {card.orders.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">
                Diagnostic orders
              </div>
              <ul className="space-y-1">
                {card.orders.map((o) => (
                  <li key={o.id} className="text-xs text-[#A3ACA7] flex items-start gap-2">
                    <FlaskConical size={11} className="mt-0.5" style={{ color: accent }} />
                    <span>
                      <span className="text-white">
                        {(o.tests || []).map((t) => t.code).join(", ")}
                      </span>
                      {" · "}<em style={{
                        color: o.urgency === "stat" ? "#F87171"
                             : o.urgency === "urgent" ? "#FBBF24" : "#4ADE80"
                      }}>{o.urgency}</em>{" · "}{o.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function MiniCounter({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-[#1C201E] py-1.5 px-2">
      <div className="font-heading text-base" style={{ color: accent }}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#7A8480]">{label}</div>
    </div>
  );
}


const HIGHLIGHT_ICONS = { rx: Pill, note: FileText, test: FlaskConical, referral: ArrowUpRight, diagnosis: Activity };
const HIGHLIGHT_ACCENT = { rx: "#8B5CF6", note: "#94A3B8", test: "#FBBF24", referral: "#EC4899", diagnosis: "#38BDF8" };


function CareUpdateCard({ update, loading, onRefresh }) {
  const s = update?.summary;
  const highlights = s?.highlights || [];
  const empty = !loading && (!s || (!highlights.length && !s.headline));

  return (
    <div className="rounded-2xl border p-4 space-y-3"
         style={{
           background: "linear-gradient(120deg, rgba(139,92,246,0.06) 0%, rgba(74,222,128,0.06) 100%)",
           borderColor: "rgba(139,92,246,0.35)",
         }}
         data-testid="ct-care-update">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
             style={{ background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.45)" }}>
          <Sparkles size={16} className="text-[#8B5CF6]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-[#8B5CF6]">
              Your Care Update
            </span>
            {update?.cached && (
              <span className="text-[9px] text-[#7A8480] uppercase tracking-wider">· cached</span>
            )}
          </div>
          <div className="text-base text-white leading-snug mt-1" data-testid="ct-care-update-headline">
            {loading
              ? <span className="text-[#7A8480]">Preparing your update…</span>
              : (s?.headline || "No new activity from your care team.")}
          </div>
        </div>
        <button onClick={onRefresh}
                disabled={loading}
                data-testid="ct-care-update-refresh"
                className="text-[10px] px-2.5 py-1.5 rounded-full border border-[#8B5CF6]/45 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors flex items-center gap-1 disabled:opacity-50 shrink-0">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {!loading && !empty && highlights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ct-care-update-highlights">
          {highlights.map((h, i) => {
            const Icon = HIGHLIGHT_ICONS[h.icon] || FileText;
            const accent = HIGHLIGHT_ACCENT[h.icon] || "#94A3B8";
            return (
              <div key={i} className="rounded-xl bg-[#0F1312]/70 border border-[#272E2B] p-3 flex items-start gap-2.5"
                   data-testid={`ct-care-update-hl-${i}`}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                     style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
                  <Icon size={12} style={{ color: accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white leading-tight">{h.title}</div>
                  {h.detail && (
                    <div className="text-xs text-[#A3ACA7] leading-snug mt-0.5 line-clamp-2">
                      {h.detail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && s?.next_step && (
        <div className="flex items-center gap-2 text-xs text-[#A3ACA7] pt-2 border-t border-[#272E2B]/60">
          <ArrowRight size={12} className="text-[#4ADE80] shrink-0" />
          <span><strong className="text-white">Next step:</strong> {s.next_step}</span>
        </div>
      )}
    </div>
  );
}




function TimelineRow({ ev }) {
  const iconMap = { encounter: Activity, prescription: Pill, order: FlaskConical, result: Sparkles };
  const Icon = iconMap[ev.kind] || AlertCircle;
  const specialty = ev.specialty || "general";
  const accent = SPEC_ACCENT[specialty] || "#94A3B8";
  const date = (ev.at || "").slice(0, 10);
  return (
    <div className="flex items-start gap-2.5 text-sm" data-testid={`ct-tl-${ev.kind}`}>
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
           style={{ background: `${accent}18`, border: `1px solid ${accent}40` }}>
        <Icon size={12} style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 text-[10px] text-[#7A8480] uppercase tracking-wider">
          <span>{ev.kind} · {specialty.replace("_", " ")}</span>
          <span>{date}</span>
        </div>
        <div className="text-white text-sm leading-tight">{ev.title}</div>
        <div className="text-[10px] text-[#7A8480] mt-0.5">
          {ev.actor_name}
          {ev.status && <> · {ev.status}</>}
        </div>
      </div>
    </div>
  );
}
